import { IconButton, Menu, MenuItem, Tooltip, useTheme } from '@mui/material';
import { DocumentDownload } from 'iconsax-react';
import { DateTime } from 'luxon';
import { type ReactElement, useState } from 'react';

import { hasValue } from '@core/utils/equality';
import { type ExportColumn, ExportFormat, sheetFromColumns } from '@core/utils/tableExport';
import { useToast } from '@web/providers/ToastProvider/useToast';
import type { FilterByOpts } from '@web/utils/ApiClient';
import { downloadCsv, downloadWorkbook } from '@web/utils/sheetDownload';

import type { Column } from '../../TableDataModel';
import type { ReadableSet } from '../../useApiTableData';

type Props<TRow extends { id: string }> = {
  renderedColumns: ReadableSet<Column<TRow>>;
  allFilters: FilterByOpts<TRow>[];
  getAllRows: () => Promise<TRow[]>;
  title: string;
};

export const ExportButton = <TRow extends { id: string }>({
  renderedColumns,
  allFilters,
  getAllRows,
  title,
}: Props<TRow>): ReactElement => {
  const theme = useTheme();
  const toast = useToast();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const exportAs = async (format: ExportFormat): Promise<void> => {
    setMenuAnchor(null);
    setIsExporting(true);
    try {
      const columns = [...renderedColumns]
        .filter((column) => column.isExportDisabled !== true)
        .map((column): ExportColumn<TRow> => ({
          header:
            typeof column.label === 'function'
              ? column.label(allFilters.filter((f) => 'name' in f && f.name === column.id))
              : column.label,
          getValue: column.toCell,
        }));
      const sheet = sheetFromColumns(title, columns, await getAllRows());
      const fileName = `${title} ${DateTime.now().toFormat('yyyy-MM-dd')}.${format}`;
      if (format === ExportFormat.Csv) {
        downloadCsv(fileName, sheet);
      } else {
        await downloadWorkbook(fileName, [sheet]);
      }
    } catch {
      toast.error({ message: 'Failed to export, please try again.' });
    } finally {
      setIsExporting(false);
    }
  };

  // The span keeps the tooltip working while the button is disabled.
  return (
    <>
      <Tooltip title='Export'>
        <span>
          <IconButton
            aria-label='Export'
            onClick={(event): void => setMenuAnchor(event.currentTarget)}
            disabled={isExporting}
          >
            <DocumentDownload variant='TwoTone' color={theme.palette.SlabIndigo['700']} />
          </IconButton>
        </span>
      </Tooltip>
      <Menu anchorEl={menuAnchor} open={hasValue(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={() => exportAs(ExportFormat.Xlsx)}>Excel (.xlsx)</MenuItem>
        <MenuItem onClick={() => exportAs(ExportFormat.Csv)}>CSV (.csv)</MenuItem>
      </Menu>
    </>
  );
};
